import "@assets/css/bootstrap-grid.css";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton, SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import {
  changeSymbolstoDash,
  isEditorContentEmpty,
  makeSlug,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  testimonialDescripionSchema,
  TestimonialDescriptionFormTypes,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { TestimonialData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

const DescriptionTestimonialForm: React.FC = () => {
  const pathname = usePathname();
  const { searchParams, handleAction } = useHandleAction({});
  const queryClient = useQueryClient();
  const { setIsFormPending } = useSelectionStore();

  /* avoid repetation */
  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(testimonialDescripionSchema),
  });
  useEffect(() => {
    if (pageMode === "add") {
      setValue("slug", changeSymbolstoDash(makeSlug(watch("name"))));
    }
  }, [watch("name")]);
  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<TestimonialData>
  >({
    endpoint: endpoint["testimonial"],
    id: pageItem || "",
    key: "testimonial",
    params: {
      id: pageItem || "",
    },
    enabled: pageItem ? true : false,
  });

  const handleMutate = (data: any) => {
    if (pageItem) {
      data = {
        ...data,
        slug: watch("slug"),
        testimonial_type: "description",
        id: pageItem || "",
      };
    }
    // if (!(data.image instanceof File || data.image instanceof Blob))
    //   delete data.image;
    data = { ...data, testimonial_type: "description" };
    if (data.testimonial_type == "description") {
      delete data.video;
      data.video_url = "";
    }
    return {
      data,
      endpoint: endpoint["testimonial"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };

  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    setIsFormPending(false);
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });

      handleAction({ type: "page-close" });
      showToast({
        description: `${pathname} ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };

  const handleError = async (error: any) => {
    setIsFormPending(false);
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
    console.log(error, "error00");
  };
  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;

      if (pageMode === "add") {
        return reset({});
      } else if (firstResult) {
        reset(() => ({
          slug: firstResult?.slug,
          description: isEditorContentEmpty(firstResult?.description)
            ? ""
            : firstResult?.description,
          image: firstResult?.image || "",
          name: firstResult?.name || "",
          role: firstResult?.role || "",
          header_code: firstResult?.header_code || "",
          footer_code: firstResult?.footer_code || "",
        }));
      }
    }
  }, [fetchData?.data, fetchPending]);

  const { mutate } = usePostData();

  const onSubmit: SubmitHandler<TestimonialDescriptionFormTypes> = (data) => {
    setIsFormPending(true);
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "Name",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Name",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "slug",
        label: "Slug",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Slug",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "role",
        label: "Role",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Role",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "description",
        label: "Description",
        colSpan: 6,
        type: "editor",
        height: 150,
        placeholder: "Enter Description",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },

      {
        name: "image",
        label: "Upload Image",
        colSpan: 6,
        type: "cropper",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
      name: "header_code",
      label: "Header Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "footer_code",
      label: "Footer Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    ],
    [control, errors, setValue]
  );

  if (fetchPending && searchParams.get("modal-item"))
    return (
      <>
        <SkeletonTwo />
      </>
    );

  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <>
      {" "}
      <form
        id={pathname + "description"}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default DescriptionTestimonialForm;
